import { useEffect } from "react";
import { Form, useActionData, useNavigation, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Button, LinkButton } from "../../components/common";
import { LanguageSwitcher } from "../../components/common/LanguageSwitcher";
import type { ActionResult } from "../../routes/actions";
import "./auth.css";

export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigation = useNavigation();
  const actionData = useActionData() as ActionResult | undefined;
  const isSubmitting = navigation.state === "submitting";

  useEffect(() => {
    if (!actionData) return;

    if (actionData.error) {
      toast.error(actionData.error);
    }
  }, [actionData]);

  if (!token) {
    return (
      <div className="auth-container">
        <div className="auth-language-switcher">
          <LanguageSwitcher variant="standalone" />
        </div>
        <div className="auth-card">
          <div className="auth-header">
            <h1>{t("auth.resetPassword.invalidLink", "Invalid Link")}</h1>
          </div>

          <div className="error-message">
            <strong>
              {t("auth.resetPassword.invalidLinkTitle", "Invalid Password Reset Link")}
            </strong>
            <p style={{ marginTop: "0.5rem", marginBottom: 0 }}>
              {t(
                "auth.resetPassword.invalidLinkMessage",
                "This password reset link is invalid or malformed.",
              )}
            </p>
          </div>

          <div style={{ marginTop: "1.5rem" }}>
            <LinkButton to="/forgot-password" variant="primary" fullWidth>
              {t("auth.resetPassword.requestNewLink", "Request New Reset Link")}
            </LinkButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-language-switcher">
        <LanguageSwitcher variant="standalone" />
      </div>
      <div className="auth-card">
        <div className="auth-header">
          <h1>{t("auth.resetPassword.title")}</h1>
          <p>{t("auth.resetPassword.subtitle")}</p>
        </div>

        <Form method="post" className="auth-form">
          <div className={`form-group ${actionData?.fieldErrors?.password ? "error" : ""}`}>
            <label htmlFor="password">{t("auth.resetPassword.passwordLabel")}</label>
            <input
              id="password"
              name="password"
              type="password"
              placeholder={t("auth.resetPassword.passwordPlaceholder")}
              autoComplete="new-password"
            />
            {actionData?.fieldErrors?.password && (
              <span className="form-error">{actionData.fieldErrors.password}</span>
            )}
          </div>

          <div className={`form-group ${actionData?.fieldErrors?.confirmPassword ? "error" : ""}`}>
            <label htmlFor="confirmPassword">{t("auth.resetPassword.confirmPasswordLabel")}</label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder={t("auth.resetPassword.confirmPasswordPlaceholder")}
              autoComplete="new-password"
            />
            {actionData?.fieldErrors?.confirmPassword && (
              <span className="form-error">{actionData.fieldErrors.confirmPassword}</span>
            )}
          </div>

          <Button
            type="submit"
            variant="primary"
            fullWidth
            loading={isSubmitting}
            loadingText={t("auth.resetPassword.submitting")}
          >
            {t("auth.resetPassword.submitButton")}
          </Button>
        </Form>

        <div className="auth-links">
          <Link to="/login" className="auth-link">
            {t("auth.resetPassword.rememberPassword", "Remember your password? Sign in")}
          </Link>
        </div>
      </div>
    </div>
  );
}
